import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useIsSectionMobile from "./useIsSectionMobile";

const SOUND_KEY = "video_screen_sound_on_v1";

const isAllowedOnMobile = (item) => {
  const f = item?.format;
  return f === "VERTICAL" || f === "SQR";
};

const safeIndex = (n, len) => {
  if (len <= 0) return 0;
  const mod = n % len;
  return mod < 0 ? mod + len : mod;
};

const tryPlay = async (videoEl) => {
  if (!videoEl) return false;
  try {
    const p = videoEl.play();
    if (p && typeof p.then === "function") await p;
    return true;
  } catch (e) {
    return false;
  }
};

const pauseHard = (videoEl) => {
  if (!videoEl) return;
  try {
    videoEl.pause();
  } catch (e) {
    // ignore
  }
};

export default function useVideoScreenLogic({ data, videoRef }) {
  const wrapRef = useRef(null);
  const observerRef = useRef(null);
  const timerRef = useRef(null);

  // במפורש: בסקשן הזה 1400 ומטה = מובייל
  const isMobile = useIsSectionMobile(1400);

  const [index, setIndex] = useState(0);

  // סאונד
  const [isMuted, setIsMuted] = useState(true);
  const [userMutedManually, setUserMutedManually] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // אם רצינו סאונד, אבל במעבר OS/מדיניות כיבו לנו → נעצור ונדרוש PLAY
  const [requiresUserRestartForSound, setRequiresUserRestartForSound] =
    useState(false);

  const items = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    if (!isMobile) return list;
    return list.filter(isAllowedOnMobile);
  }, [data, isMobile]);

  const current = useMemo(() => items[index] || null, [items, index]);

  // טוענים העדפת סאונד (אם קיימת)
  useEffect(() => {
    try {
      const v = localStorage.getItem(SOUND_KEY);
      if (v === "1") {
        setIsMuted(false);
        setUserMutedManually(false);
      }
      if (v === "0") {
        setIsMuted(true);
        setUserMutedManually(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // אם הפילטר משתנה (מובייל/דסקטופ) ודואגים שהאינדקס חוקי
  useEffect(() => {
    const len = items.length;
    if (len <= 0) return;
    setIndex((prev) => safeIndex(prev, len));
  }, [items]);

  const persistSound = useCallback((muted, manualFlag) => {
    try {
      if (manualFlag) localStorage.setItem(SOUND_KEY, "0");
      else localStorage.setItem(SOUND_KEY, muted ? "0" : "1");
    } catch (e) {
      // ignore
    }
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prevMuted) => {
      const nextMuted = !prevMuted;

      // פעולה ידנית של המשתמש
      const nextManual = nextMuted; // אם כיבה → manual true
      setUserMutedManually(nextManual);
      persistSound(nextMuted, nextManual);

      // אם חזרנו לסאונד, לא חייבים restart
      if (!nextMuted) setRequiresUserRestartForSound(false);

      // ננסה לנגן כדי שישמע
      if (!nextMuted) {
        const el = videoRef.current;
        if (el) el.muted = false;
        void tryPlay(el).then((ok) => setIsPlaying(ok));
      }

      return nextMuted;
    });
  }, [persistSound, videoRef]);

  // אינטראקציה שמדליקה סאונד אוטומטית (אם המשתמש לא כיבה ידנית)
  const markInteractionThatShouldEnableSound = useCallback(() => {
    if (userMutedManually) return;

    setIsMuted(false);
    setUserMutedManually(false);
    persistSound(false, false);

    // אם היינו במצב "צריך restart בשביל סאונד" – אינטראקציה מבטלת את זה
    setRequiresUserRestartForSound(false);
  }, [persistSound, userMutedManually]);

  const goNext = useCallback(() => {
    const len = items.length;
    if (len <= 0) return;
    setIndex((prev) => safeIndex(prev + 1, len));
  }, [items.length]);

  const goPrev = useCallback(() => {
    const len = items.length;
    if (len <= 0) return;
    setIndex((prev) => safeIndex(prev - 1, len));
  }, [items.length]);

  const togglePlayPause = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;

    if (isPlaying) {
      pauseHard(el);
      setIsPlaying(false);
      return;
    }

    // לוחץ PLAY → אמור להדליק סאונד אוטומטי (אלא אם כיבה)
    if (!userMutedManually) {
      setIsMuted(false);
      setUserMutedManually(false);
      persistSound(false, false);
      el.muted = false;
    }

    setRequiresUserRestartForSound(false);
    void tryPlay(el).then((ok) => setIsPlaying(ok));
  }, [isPlaying, persistSound, userMutedManually, videoRef]);

  // עצירה מלאה כשלא visible
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (!isVisible) {
      pauseHard(el);
      setIsPlaying(false);
    }
  }, [isVisible, videoRef]);

  // קובעים muted בפועל
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = Boolean(isMuted);
  }, [isMuted, videoRef]);

  // Attempt autoplay כאשר נהיה visible / מתחלף סרטון
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (!isVisible) return;
    if (requiresUserRestartForSound) return; // במצב הזה לא עושים autoplay

    // אם המשתמש לא כיבה ידנית ואנחנו "רוצים סאונד" → ננסה להפעיל עם סאונד
    const wantsSound = !userMutedManually && !isMuted;

    if (wantsSound) {
      el.muted = false;
    } else {
      el.muted = true;
    }

    void tryPlay(el).then((ok) => {
      setIsPlaying(ok);

      // אם רצינו סאונד, אבל בפועל הוא נשאר muted → עוצרים ודורשים PLAY מחדש
      if (ok && wantsSound && el.muted === true) {
        pauseHard(el);
        setIsPlaying(false);
        setRequiresUserRestartForSound(true);
      } else {
        setRequiresUserRestartForSound(false);
      }
    });
  }, [index, isVisible, isMuted, userMutedManually, requiresUserRestartForSound, videoRef]);

  // טיימר לפי seconds → מעבר אוטומטי
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!isVisible) return;
    if (!isPlaying) return;
    if (requiresUserRestartForSound) return;
    if (!current) return;

    const sec = Number(current.seconds);
    if (!Number.isFinite(sec) || sec <= 0) return;

    timerRef.current = setTimeout(() => {
      // במעבר אוטומטי: אם המשתמש לא כיבה ידנית → זה נחשב "מערכת/דפדוף" שממשיכה את הסאונד
      // כלומר: אם סאונד היה ON → נשאיר ON, ואם OS יכבה → הלוגיקה תעצור ותדרוש PLAY מחדש.
      goNext();
    }, sec * 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [current, isVisible, isPlaying, requiresUserRestartForSound, goNext]);

  // IntersectionObserver bind
  const bindVisibilityToElement = useCallback((node) => {
    wrapRef.current = node;

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries[0];
        const v =
          Boolean(entry && entry.isIntersecting) &&
          (entry.intersectionRatio || 0) >= 0.35;
        setIsVisible(v);
      },
      { threshold: [0, 0.15, 0.35, 0.6, 1] }
    );

    obs.observe(node);
    observerRef.current = obs;
  }, []);

  // swipe helpers
  const onSwipeNext = useCallback(() => {
    markInteractionThatShouldEnableSound();
    goNext();
  }, [goNext, markInteractionThatShouldEnableSound]);

  const onSwipePrev = useCallback(() => {
    markInteractionThatShouldEnableSound();
    goPrev();
  }, [goPrev, markInteractionThatShouldEnableSound]);

  return {
    items,
    index,
    current,
    isMobile,
    isVisible,
    isPlaying,
    isMuted,
    userMutedManually,
    requiresUserRestartForSound,

    goNext,
    goPrev,
    togglePlayPause,
    toggleMute,

    onSwipeNext,
    onSwipePrev,

    markInteractionThatShouldEnableSound,
    bindVisibilityToElement,
  };
}
